import {
  ArrowSquareOut,
  CheckCircle,
  Clock,
  type Icon,
  MagnifyingGlass,
  Warning,
  XCircle,
} from '@phosphor-icons/react';
import type {
  AccountPostMetricsDto,
  ScheduledPostAccountDto,
  ScheduledPostDto,
} from '@reelplicate/api-client/model';
import { useMarkAccountPosted } from '@reelplicate/api-client/scheduled-posts';
import {
  Button,
  cn,
  Field,
  Inline,
  Link,
  Stack,
  TextInput,
  toast,
  Tooltip,
} from '@reelplicate/ui';
import { useState } from 'react';

import { formatCount } from '@/features/analytics/format';
import { formatWhen } from '@/lib/format-when';

import {
  type AccountPostState,
  accountPostState,
  API_PLATFORM_NAMES,
  toPlatform,
} from './calendar-model';
import { toLocalInput } from './local-input';
import { PlatformDot } from './platform-stack';

const STATES: Record<
  AccountPostState,
  { icon: Icon; tone: string; text: (name: string) => string }
> = {
  published: {
    icon: CheckCircle,
    tone: 'text-status-success-fg',
    text: () => 'Published',
  },
  upcoming: { icon: Clock, tone: 'text-text-secondary', text: () => 'Scheduled' },
  checking: {
    icon: MagnifyingGlass,
    tone: 'text-text-secondary',
    text: (name) => `Looking for it on ${name}`,
  },
  missing: { icon: Warning, tone: 'text-status-warning-fg', text: () => 'Not found yet' },
  failed: { icon: XCircle, tone: 'text-status-danger-fg', text: () => 'Failed' },
  draft: { icon: Clock, tone: 'text-text-secondary', text: () => 'Draft' },
  cancelled: { icon: XCircle, tone: 'text-text-tertiary', text: () => 'Cancelled' },
};

const METRICS = [
  { key: 'views', label: 'Views' },
  { key: 'likes', label: 'Likes' },
  { key: 'comments', label: 'Comments' },
  { key: 'shares', label: 'Shares' },
  { key: 'saves', label: 'Saves' },
] as const;

/**
 * One row per account a post goes to: where it stands there (published,
 * scheduled, being looked for, not found yet, failed and why), a link to it on
 * the platform, its numbers there, and marking it posted by hand.
 */
export function PostAccounts({
  workspaceId,
  post,
  now,
  onChanged,
}: {
  workspaceId: string;
  post: ScheduledPostDto;
  now: Date;
  /** After an account was marked posted: refetch the post. */
  onChanged: () => Promise<unknown>;
}) {
  const [marking, setMarking] = useState<string | null>(null);
  const scheduledAt = new Date(post.scheduledAt);
  return (
    <Stack as="section" aria-label="Accounts" gap={2}>
      <h3 className="text-label-md text-text-primary">Accounts</h3>
      <Stack as="ul" gap={2}>
        {post.accounts.map((account) => (
          <AccountRow
            key={account.id}
            workspaceId={workspaceId}
            postId={post.id}
            account={account}
            state={accountPostState(account, scheduledAt, now)}
            marking={marking === account.id}
            onMark={(open) => setMarking(open ? account.id : null)}
            onChanged={onChanged}
          />
        ))}
      </Stack>
    </Stack>
  );
}

function AccountRow({
  workspaceId,
  postId,
  account,
  state,
  marking,
  onMark,
  onChanged,
}: {
  workspaceId: string;
  postId: string;
  account: ScheduledPostAccountDto;
  state: AccountPostState;
  marking: boolean;
  onMark: (open: boolean) => void;
  onChanged: () => Promise<unknown>;
}) {
  const name = API_PLATFORM_NAMES[account.platform];
  const platform = toPlatform(account.platform);
  const { icon: StateIcon, tone, text } = STATES[state];
  const canMark = state !== 'published' && state !== 'draft' && state !== 'cancelled';
  const detail =
    state === 'published'
      ? account.publishedAt && formatWhen(account.publishedAt)
      : state === 'failed'
        ? (account.failureReason ?? 'It didn’t go out here')
        : state === 'missing'
          ? `Not on ${name} yet; it’s marked failed if it isn’t there six hours after its time`
          : null;
  return (
    <li
      aria-label={`${name} ${account.handle}`}
      className="border-border-subtle rounded-md border p-3"
    >
      <Stack gap={3}>
        <Inline gap={3} align="between" alignY="top" wrap>
          <Inline gap={2} className="min-w-0">
            {platform && <PlatformDot platform={platform} />}
            <Stack gap={0} className="min-w-0">
              <span className="text-label-md text-text-primary truncate">
                {account.handle}
              </span>
              <p className={cn('text-caption', tone)}>
                <StateIcon
                  aria-hidden
                  weight="fill"
                  className="mr-1 inline size-3.5 align-[-3px]"
                />
                {text(name)}
                {detail && (state === 'published' ? ` ${detail}` : `: ${detail}`)}
              </p>
            </Stack>
          </Inline>
          <Inline gap={2}>
            {account.url && (
              <Link
                href={account.url}
                target="_blank"
                rel="noreferrer"
                size="sm"
                trailingIcon={ArrowSquareOut}
              >
                View on {name}
              </Link>
            )}
            {canMark && !marking && (
              <Button size="sm" variant="outline" onClick={() => onMark(true)}>
                Mark posted
              </Button>
            )}
          </Inline>
        </Inline>
        {account.metrics && <AccountMetrics metrics={account.metrics} name={name} />}
        {marking && (
          <MarkAccountPostedForm
            workspaceId={workspaceId}
            postId={postId}
            account={account}
            name={name}
            onClose={() => onMark(false)}
            onChanged={onChanged}
          />
        )}
      </Stack>
    </li>
  );
}

/** Its numbers on one account; "—" for what the platform doesn't report. */
function AccountMetrics({
  metrics,
  name,
}: {
  metrics: AccountPostMetricsDto;
  name: string;
}) {
  return (
    <Stack gap={1}>
      <dl className="tablet:grid-cols-5 grid grid-cols-3 gap-2">
        {METRICS.map(({ key, label }) => {
          const value = metrics[key];
          return (
            <div key={key}>
              <dt className="text-caption text-text-tertiary">{label}</dt>
              <dd className="text-label-md text-text-primary tabular-nums">
                {value === null ? (
                  <Tooltip content={`Not available from ${name}`}>
                    <span
                      tabIndex={0}
                      aria-label={`Not available from ${name}`}
                      className="text-text-tertiary focus-visible:shadow-focus-ring rounded-xs outline-none"
                    >
                      —
                    </span>
                  </Tooltip>
                ) : (
                  formatCount(value)
                )}
              </dd>
            </div>
          );
        })}
      </dl>
      <span className="text-caption text-text-tertiary">
        From {name} · {formatWhen(metrics.measuredAt)}
      </span>
    </Stack>
  );
}

/** "Mark posted" on one account: when it went out there and, optionally, its link. */
function MarkAccountPostedForm({
  workspaceId,
  postId,
  account,
  name,
  onClose,
  onChanged,
}: {
  workspaceId: string;
  postId: string;
  account: ScheduledPostAccountDto;
  name: string;
  onClose: () => void;
  onChanged: () => Promise<unknown>;
}) {
  const mark = useMarkAccountPosted();
  // When the form opened: the latest it can have gone out.
  const [openedAt] = useState(() => new Date());
  const [when, setWhen] = useState(() => toLocalInput(openedAt));
  const [url, setUrl] = useState('');
  const postedAt = when ? new Date(when) : null;
  const inFuture = postedAt !== null && postedAt > openedAt;
  const badUrl = url.trim() !== '' && !/^https:\/\/\S+$/.test(url.trim());

  const save = async () => {
    if (!postedAt || inFuture || badUrl) return;
    try {
      await mark.mutateAsync({
        id: workspaceId,
        postId,
        accountId: account.id,
        data: {
          postedAt: postedAt.toISOString(),
          ...(url.trim() ? { url: url.trim() } : {}),
        },
      });
    } catch {
      return; // Shown as a toast.
    }
    await onChanged();
    toast.success(`Marked as posted on ${name}`);
    onClose();
  };

  return (
    <Stack gap={3} className="bg-bg-subtle rounded-sm p-3">
      <div className="tablet:grid-cols-2 grid grid-cols-1 gap-3">
        <Field
          label="Posted at"
          helper="In your local time"
          error={inFuture ? 'Pick a time that has already passed' : undefined}
        >
          <TextInput
            size="sm"
            type="datetime-local"
            value={when}
            max={toLocalInput(openedAt)}
            onChange={(event) => setWhen(event.target.value)}
          />
        </Field>
        <Field
          label={`Link on ${name}`}
          optional
          error={badUrl ? 'Use the post’s https:// link' : undefined}
        >
          <TextInput
            size="sm"
            type="url"
            inputMode="url"
            placeholder="https://"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
          />
        </Field>
      </div>
      <Inline gap={2} align="right">
        <Button size="sm" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="sm"
          leadingIcon={CheckCircle}
          disabled={!postedAt || inFuture || badUrl}
          loading={mark.isPending}
          onClick={() => void save()}
        >
          Mark posted on {name}
        </Button>
      </Inline>
    </Stack>
  );
}
